import { useCallback, useMemo, useRef } from "react"

import { useRouter as useNextRouter } from "@/common/vite-shims/next/router"

import type { AppRouter, NavOptions, NavTarget } from "./types"

/**
 * Framework-neutral router hook. Adapts the React Router-backed shim
 * (`@/common/vite-shims/next/router`) to {@link AppRouter}.
 *
 * Consumers MUST import from here (or `@/common/navigation`) and never from
 * `next/router` — an ESLint rule enforces this.
 */
export function useRouter(): AppRouter {
  const next = useNextRouter()

  // Hold the latest Next router in a ref so the navigation callbacks stay
  // referentially stable (empty dep arrays) without ever reading a stale router.
  const nextRef = useRef(next)
  nextRef.current = next

  const push = useCallback<AppRouter["push"]>(
    (target: string | NavTarget, opts?: NavOptions) =>
      nextRef.current.push(target, undefined, opts),
    []
  )
  const replace = useCallback<AppRouter["replace"]>(
    (target: string | NavTarget, opts?: NavOptions) =>
      nextRef.current.replace(target, undefined, opts),
    []
  )
  const back = useCallback(() => nextRef.current.back(), [])
  const reload = useCallback(() => nextRef.current.reload(), [])

  const { query, pathname, asPath, isReady } = next

  // Return a stable object whose identity changes only when the route state
  // changes — never on unrelated re-renders. Consumers that list `router` in an
  // effect/callback dep array (e.g. redirect-on-mount) must not re-run every
  // render. Next's own `query` reference is stable between renders, so memoizing
  // on these values reproduces the underlying router's stability.
  return useMemo<AppRouter>(
    () => ({ query, pathname, asPath, isReady, push, replace, back, reload }),
    [query, pathname, asPath, isReady, push, replace, back, reload]
  )
}
